stimulusApp.register(
  "search--recent-searches",
  class extends Stimulus.Controller {
    // Expected to be attached directly to a <turbo-frame> element.
    static outlets = ["search--location-form", "search--travel-date-picker"]

    static values = {
      endpoint: String,
      storageKey: String,
      limit: { type: Number, default: 3 },
      maxAgeDays: { type: Number, default: 30 },
    }

    #onPageShow = (event) => {
      if (event.persisted) this.load()
    }

    connect() {
      this.load()
      window.addEventListener("pageshow", this.#onPageShow)
    }

    disconnect() {
      window.removeEventListener("pageshow", this.#onPageShow)
    }

    clear() {
      if (this.storageKeyValue) {
        localStorage.removeItem(this.storageKeyValue)
      }
      this.#clearFrame()
    }

    restore(event) {
      const { location, checkin, checkout } = event.params
      const normalizedLocation = this.#normalizeLocationParam(location)

      if (this.hasSearchLocationFormOutlet && normalizedLocation) {
        this.searchLocationFormOutlet.setDestination(
          this.#restorableLocation(normalizedLocation),
          true,
        )
      }

      if (this.hasSearchTravelDatePickerOutlet && (checkin || checkout)) {
        this.searchTravelDatePickerOutlet.setDates(
          checkin || "",
          checkout || "",
        )
      }

      this.#replaceGroupConfiguration(event.currentTarget)
    }

    load() {
      if (!this.endpointValue || !this.storageKeyValue) {
        return
      }

      const searches = this.#readSearches()
      if (!searches.length) {
        this.#clearFrame()
        return
      }

      const params = new URLSearchParams({
        searches: JSON.stringify(searches),
      })
      this.element.setAttribute(
        "src",
        `${this.endpointValue}?${params.toString()}`,
      )
    }

    #readSearches() {
      let parsed
      try {
        parsed = JSON.parse(localStorage.getItem(this.storageKeyValue) || "[]")
      } catch (_error) {
        return []
      }

      if (!Array.isArray(parsed)) {
        return []
      }

      const maxAgeMs = this.maxAgeDaysValue * 24 * 60 * 60 * 1000
      const oldestTimestamp = Date.now() - maxAgeMs

      return parsed
        .filter((search) => this.#validSearch(search))
        .filter((search) => Number(search.timestamp) >= oldestTimestamp)
        .slice(0, this.limitValue)
    }

    #validSearch(search) {
      if (!search || typeof search !== "object") return false
      const destination =
        search.selectedDestination || search["selected-destination"]
      if (!destination || typeof destination !== "object") {
        return false
      }
      return Boolean(destination.name || destination["location-name"])
    }

    #normalizeLocationParam(location) {
      if (!location) return null
      if (typeof location === "object") return location
      if (typeof location !== "string") return null
      try {
        const parsed = JSON.parse(location)
        return parsed && typeof parsed === "object" ? parsed : null
      } catch (_error) {
        return null
      }
    }

    #restorableLocation(location) {
      return {
        ...location,
        submitPath: location.submitPath || null,
      }
    }

    #clearFrame() {
      this.element.removeAttribute("src")
      this.element.innerHTML = ""
    }

    #replaceGroupConfiguration(link) {
      const template = link?.querySelector(
        "template[data-search--recent-searches-group-configuration-template]",
      )
      const replacement = template?.content?.firstElementChild?.cloneNode(true)
      if (!replacement) return

      const current = this.#currentGroupConfigurationSection()
      if (!current) return
      current.replaceWith(replacement)
    }

    #currentGroupConfigurationSection() {
      return this.element
        .closest(".place-search--container")
        ?.querySelector(
          ".place-search > .search-form-slot[data-controller='search--group-configuration']",
        )
    }
  },
)
